import { useEffect, useRef, useState } from 'react';
import { useAccessibility } from '../../contexts/AccessibilityContext';

/**
 * Floating "Read & Adapt" launcher + reading-adaptations panel. Rendered
 * app-wide by the AccessibilityProvider so it's reachable from every portal.
 * Non-modal disclosure (aria-modal="false") by design — it never traps focus.
 */

const FONT_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'sans', label: 'Sans' },
  { value: 'serif', label: 'Serif' },
  { value: 'dyslexic', label: 'Dyslexia-friendly style' },
];

const SPACING_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 1, label: 'More' },
  { value: 2, label: 'Most' },
];

const OVERLAY_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'cream', label: 'Cream' },
  { value: 'yellow', label: 'Yellow' },
  { value: 'rose', label: 'Rose' },
  { value: 'blue', label: 'Blue' },
  { value: 'green', label: 'Green' },
  { value: 'grey', label: 'Grey' },
];

export default function AccessibilityPanel() {
  const { prefs, setPref, reset } = useAccessibility();
  const [open, setOpen] = useState(false);
  const [announce, setAnnounce] = useState('');
  const panelRef = useRef(null);
  const launcherRef = useRef(null);

  // Close on Escape; restore focus to the launcher.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        launcherRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const change = (key, value, message) => {
    setPref(key, value);
    if (message) setAnnounce(message);
  };

  const textPct = Math.round(prefs.fontScale * 100);

  return (
    <>
      <button
        ref={launcherRef}
        type="button"
        className="a11y-launcher"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Reading adaptations"
        title="Reading adaptations"
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden="true">&#9855;</span>
      </button>

      {open && (
        <div
          ref={panelRef}
          className="a11y-panel"
          role="dialog"
          aria-modal="false"
          aria-label="Reading adaptations"
        >
          <h2>Read &amp; Adapt</h2>
          <p className="a11y-sub">Saved on this device.</p>

          {/* Font type */}
          <div className="a11y-group">
            <span className="a11y-group-label" id="a11y-font">Font style</span>
            <div className="a11y-seg a11y-seg--wrap" role="group" aria-labelledby="a11y-font">
              {FONT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  aria-pressed={prefs.fontType === opt.value}
                  onClick={() => change('fontType', opt.value, `Font style ${opt.label}`)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Text size */}
          <div className="a11y-group">
            <label className="a11y-group-label" htmlFor="a11y-textsize">
              Text size <span className="a11y-value">{textPct}%</span>
            </label>
            <input
              id="a11y-textsize"
              type="range"
              min="90"
              max="150"
              step="10"
              value={textPct}
              onChange={(e) => change('fontScale', Number(e.target.value) / 100, `Text size ${e.target.value} percent`)}
            />
          </div>

          {/* Reading spacing */}
          <div className="a11y-group">
            <span className="a11y-group-label" id="a11y-spacing">Reading spacing</span>
            <div className="a11y-seg" role="group" aria-labelledby="a11y-spacing">
              {SPACING_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  aria-pressed={prefs.readable === opt.value}
                  onClick={() => change('readable', opt.value, `Reading spacing ${opt.label}`)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Colour overlay */}
          <div className="a11y-group">
            <span className="a11y-group-label" id="a11y-overlay">Colour overlay</span>
            <div className="a11y-swatches" role="group" aria-labelledby="a11y-overlay">
              {OVERLAY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`a11y-swatch a11y-swatch--${opt.value}`}
                  aria-pressed={prefs.overlay === opt.value}
                  aria-label={opt.label}
                  title={opt.label}
                  onClick={() => change('overlay', opt.value, `Colour overlay ${opt.label}`)}
                />
              ))}
            </div>
            {prefs.overlay !== 'none' && (
              <label className="a11y-intensity" htmlFor="a11y-intensity">
                Tint strength <span className="a11y-value">{Math.round(prefs.overlayIntensity * 100)}%</span>
                <input
                  id="a11y-intensity"
                  type="range"
                  min="5"
                  max="30"
                  step="5"
                  value={Math.round(prefs.overlayIntensity * 100)}
                  onChange={(e) => change('overlayIntensity', Number(e.target.value) / 100, `Tint strength ${e.target.value} percent`)}
                />
              </label>
            )}
          </div>

          {/* Toggles */}
          <div className="a11y-group">
            <label className="a11y-toggle">
              <span>Calm mode <small>(hide suggestions)</small></span>
              <input
                type="checkbox"
                checked={prefs.calm}
                onChange={(e) => change('calm', e.target.checked, `Calm mode ${e.target.checked ? 'on' : 'off'}`)}
              />
            </label>
            <label className="a11y-toggle">
              <span>Reduce motion</span>
              <input
                type="checkbox"
                checked={prefs.reduceMotion}
                onChange={(e) => change('reduceMotion', e.target.checked, `Reduce motion ${e.target.checked ? 'on' : 'off'}`)}
              />
            </label>
          </div>

          <div className="a11y-panel-footer">
            <button type="button" onClick={() => { reset(); setAnnounce('Reading adaptations reset to defaults'); }}>
              Reset to defaults
            </button>
            <button type="button" onClick={() => { setOpen(false); launcherRef.current?.focus(); }}>Done</button>
          </div>

          <div className="sr-only" role="status" aria-live="polite">{announce}</div>
        </div>
      )}
    </>
  );
}
